import { getAssociatedTokenAddressSync } from '@solana/spl-token';
import { PublicKey } from '@solana/web3.js';

import config from '@/config';

const BASE_URL = config.API_BASE_URL;

export interface BurnAsset {
  mint: string;
  type: 'spl_token' | 'token_2022' | 'metadata_nft' | 'mpl_core_nft';
  isNFT: boolean;
  tokenAccount?: string;
  metadata?: string;
  masterEdition?: string;
  collection?: string;
  collectionMetadata?: string;
}

export interface BurnOrderRequest {
  tokens: Array<{
    address: string;
    tokenAccount: string;
  }>;
  metadataNFTs: Array<{
    address: string;
    metadata: string;
    tokenAccount: string;
    masterEdition: string;
    collection?: string;
    collectionMetadata?: string;
  }>;
  coreNFTs: Array<{
    address: string;
    collection?: string;
  }>;
}

export interface BurnOrderResponse {
  code: number;
  data: string; // order_id
  message?: string;
}

export interface GenerateSignatureResponse {
  code: number;
  data: {
    order_id: string;
    serialized_tx: string; // Hex-encoded partially signed transaction
  };
  message?: string;
}

/**
 * Create a burn order on the backend
 * Groups assets by type (tokens, metadata NFTs, core NFTs)
 * Derives ATAs for tokens missing tokenAccount
 */
export async function createBurnOrder(
  assets: BurnAsset[],
  walletAddress: string
): Promise<BurnOrderResponse> {
  console.log('🔥 [burn.ts] Creating burn order for', assets.length, 'assets, wallet:', walletAddress);

  // Validate all assets have valid mint addresses
  const invalidAssets = assets.filter((a) => !a || !a.mint || a.mint.trim() === '');
  if (invalidAssets.length > 0) {
    console.error('❌ [burn.ts] Found assets with invalid mint addresses:', invalidAssets);
    throw new Error(`${invalidAssets.length} asset${invalidAssets.length > 1 ? 's' : ''} have invalid mint addresses. Cannot create burn order.`);
  }

  const walletPubkey = new PublicKey(walletAddress);

  // Group assets by type and derive ATAs for tokens
  const tokens = assets
    .filter(a => !a.isNFT && (a.type === 'spl_token' || a.type === 'token_2022'))
    .map(a => {
      let tokenAccount = a.tokenAccount;

      // Derive ATA if not provided
      if (!tokenAccount) {
        console.log('🔑 [burn.ts] Deriving ATA for mint:', a.mint);
        const ata = getAssociatedTokenAddressSync(
          new PublicKey(a.mint),
          walletPubkey,
          false // allowOwnerOffCurve
        );
        tokenAccount = ata.toString();
        console.log('✅ [burn.ts] Derived ATA:', tokenAccount);
      } else {
        console.log('ℹ️ [burn.ts] Using provided tokenAccount:', tokenAccount);
      }

      return {
        address: a.mint,
        tokenAccount,
      };
    });

  const metadataNFTs = assets
    .filter(a => a.isNFT && a.type === 'metadata_nft')
    .map(a => {
      let tokenAccount = a.tokenAccount;

      // Derive ATA if not provided
      if (!tokenAccount) {
        console.log('🔑 [burn.ts] Deriving ATA for metadata NFT:', a.mint);
        const ata = getAssociatedTokenAddressSync(
          new PublicKey(a.mint),
          walletPubkey,
          false
        );
        tokenAccount = ata.toString();
        console.log('✅ [burn.ts] Derived ATA for NFT:', tokenAccount);
      }

      const nft: {
        address: string;
        metadata: string;
        tokenAccount: string;
        masterEdition: string;
        collection?: string;
        collectionMetadata?: string;
      } = {
        address: a.mint,
        metadata: a.metadata || '',
        tokenAccount,
        masterEdition: a.masterEdition || '',
      };

      // Add collection mint for validation
      if (a.collection) {
        nft.collection = a.collection;
        console.log('🏷️ [burn.ts] Including collection for validation:', a.collection);
      }

      // Add collection metadata for verification (not used currently by contracts)
      if (a.collectionMetadata) {
        nft.collectionMetadata = a.collectionMetadata;
      }

      return nft;
    });

  const coreNFTs = assets
    .filter(a => a.isNFT && a.type === 'mpl_core_nft')
    .map(a => {
      const nft: { address: string; collection?: string } = {
        address: a.mint,
      };

      // Add collection if present
      if (a.collection) {
        nft.collection = a.collection;
        console.log('🏷️ [burn.ts] Including collection for Core NFT:', a.mint);
      }

      return nft;
    });

  const requestBody: BurnOrderRequest = {
    tokens,
    metadataNFTs,
    coreNFTs,
  };

  const response = await fetch(`${BASE_URL}/chest/burn-orders`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Authentication required. Please sign in with your wallet.');
    }
    // Try to get detailed error from response body
    let errorMessage = response.statusText;
    try {
      const errorData = await response.json();
      if (errorData.message) {
        errorMessage = errorData.message;
      }
    } catch {
      // Ignore JSON parse errors
    }
    throw new Error(`Failed to create burn order: ${errorMessage}`);
  }

  return response.json();
}

/**
 * Generate a partially signed transaction for a burn order
 */
export async function generateBurnSignature(orderId: string): Promise<GenerateSignatureResponse> {
  const response = await fetch(`${BASE_URL}/tx-order/generate-signature?order_id=${encodeURIComponent(orderId)}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Authentication required. Please sign in with your wallet.');
    }
    // Try to get detailed error from response body
    let errorMessage = response.statusText;
    try {
      const errorData = await response.json();
      if (errorData.message) {
        errorMessage = errorData.message;
      }
    } catch {
      // Ignore JSON parse errors
    }
    throw new Error(`Failed to generate burn signature: ${errorMessage}`);
  }

  return response.json();
}

